import {
  ClientSubscription,
  clientSubscriptionsAtom,
  CreatePartnerSubscriptionRequest,
  dictionariesAtom,
  groupBy,
  Keys,
  MarketDataProvider,
  PartnerDataResource,
  Subscription,
} from '@eih-netbroker/common';
import { useAtom, useAtomValue } from 'jotai';
import { ReactNode, useCallback } from 'react';
import { ReactComponent as FlagDe } from '../../../assets/svg/market-flags/flag_de.svg';
import { ReactComponent as FlagEu } from '../../../assets/svg/market-flags/flag_eu.svg';
import { ReactComponent as FlagGb } from '../../../assets/svg/market-flags/flag_gb.svg';
import { ReactComponent as FlagHu } from '../../../assets/svg/market-flags/flag_hu.svg';
import { ReactComponent as FlagUnknown } from '../../../assets/svg/market-flags/flag_unknown.svg';
import { ReactComponent as FlagUs } from '../../../assets/svg/market-flags/flag_us_circle.svg';
import { useAccounts } from '../../../shared/services/useAccounts';
import { useResource, useResourceHeaders } from '../../../shared/services/useResource';

export type ClientSubscriptionWithMarketDataInfo = {
  marketDataProvider?: MarketDataProvider;
  subscription: ClientSubscription;
};

export type ActiveSubscriptionStatus = Omit<ClientSubscription, keyof Subscription> & {
  isActive: true;
};

export type InactiveSubscriptionStatus = {
  isActive: false;
};

export type SubscriptionStatus = ActiveSubscriptionStatus | InactiveSubscriptionStatus;

export type SubscriptionWithStatusInfo = Subscription & {
  statusInfo: SubscriptionStatus;
};

export type GroupedSubscriptionsWithMarketDataInfo = MarketDataProvider & {
  subscriptions: Array<Subscription>;
};

export type GroupedSubscriptionsWithMarketDataInfoAndStatusInfo = MarketDataProvider & {
  subscriptions: Array<SubscriptionWithStatusInfo>;
  discountedSubscriptions: Array<ClientSubscription>;
};

const MarketIcons: Record<string, ReactNode> = {
  [Keys.Markets.Icons.HU]: <FlagHu />,
  [Keys.Markets.Icons.DE]: <FlagDe />,
  [Keys.Markets.Icons.US]: <FlagUs />,
  [Keys.Markets.Icons.GB]: <FlagGb />,
  [Keys.Markets.Icons.EU]: <FlagEu />,
};

export const usePartnerSubscriptions = () => {
  const dictionaries = useAtomValue(dictionariesAtom);
  const partnerDataResource = useResource(PartnerDataResource);
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const { getMainClientId } = useAccounts();
  const clientSubscriptions = useAtomValue(clientSubscriptionsAtom);
  const [, setClientSubscriptions] = useAtom(clientSubscriptionsAtom);

  const loadPartnerSubscriptions = useCallback(async () => {
    const response = await partnerDataResource.getParnetSubscriptions(
      {
        clientId: getMainClientId(),
      },
      getResourceHeaderWithToken(),
    );

    setClientSubscriptions(response.data);
    return response.data;
  }, [partnerDataResource, getResourceHeaderWithToken, setClientSubscriptions, getMainClientId]);

  const getAvailableSubscriptionsGroupedBy = useCallback(async (): Promise<
    Array<GroupedSubscriptionsWithMarketDataInfo>
  > => {
    const { data } = await partnerDataResource.getPartnerAvailableSubscriptions(
      {
        clientId: getMainClientId(),
      },
      getResourceHeaderWithToken(),
    );

    const groupedSubscriptions = groupBy(data, 'marketDataProvider');

    return Object.keys(groupedSubscriptions).map(marketDataProviderId => {
      const marketDataProvider = dictionaries.marketDataProviders.find(mdp => mdp.id === marketDataProviderId);

      if (!marketDataProvider) {
        return {
          id: marketDataProviderId,
          name: marketDataProviderId,
          subscriptions: groupedSubscriptions[marketDataProviderId],
        };
      }

      return {
        ...marketDataProvider,
        subscriptions: groupedSubscriptions[marketDataProviderId],
      };
    });
  }, [dictionaries, partnerDataResource, getMainClientId, getResourceHeaderWithToken]);

  const getAvailableSubscriptionsWithStatusGroupedBy = useCallback(async (): Promise<
    Array<GroupedSubscriptionsWithMarketDataInfoAndStatusInfo>
  > => {
    const groupedSubscriptions = await getAvailableSubscriptionsGroupedBy();
    const subscribedSubscriptions = await loadPartnerSubscriptions();

    return groupedSubscriptions.map<GroupedSubscriptionsWithMarketDataInfoAndStatusInfo>(group => ({
      ...group,
      discountedSubscriptions: subscribedSubscriptions.filter(
        subscription => subscription.allowToEditByCustomer === false && subscription.marketDataProvider === group.id,
      ),
      subscriptions: group.subscriptions.map<SubscriptionWithStatusInfo>(subscription => {
        const subscribedSubscription = subscribedSubscriptions.find(
          subscribedSubscription =>
            subscribedSubscription.subscriptionPackageType === subscription.subscriptionPackageType &&
            subscribedSubscription.marketDataProvider === subscription.marketDataProvider,
        );
        return {
          ...subscription,
          statusInfo: subscribedSubscription
            ? {
                isActive: true,
                ...subscribedSubscription,
              }
            : {
                isActive: false,
              },
        };
      }),
    }));
  }, [getAvailableSubscriptionsGroupedBy, loadPartnerSubscriptions]);

  const getPartnerSubscriptionsWithMarketDataInfo = useCallback(async (): Promise<
    ClientSubscriptionWithMarketDataInfo[]
  > => {
    return clientSubscriptions.map((subscription: ClientSubscription) => {
      const marketDataProvider = dictionaries.marketDataProviders.find(
        mdp => mdp.id === subscription.marketDataProvider,
      );

      return {
        subscription,
        marketDataProvider,
      };
    });
  }, [dictionaries, clientSubscriptions]);

  const getMarketIcon = (marketDataProvider: MarketDataProvider | undefined) => {
    if (!marketDataProvider?.icon || !MarketIcons[marketDataProvider.icon]) {
      return <FlagUnknown />;
    }
    return MarketIcons[marketDataProvider.icon];
  };

  const subscribe = useCallback(
    (request: CreatePartnerSubscriptionRequest) => {
      return partnerDataResource.createSubscriptions(
        {
          clientId: getMainClientId(),
          createPartnerSubscriptionRequest: request,
        },
        getResourceHeaderWithToken(),
      );
    },
    [partnerDataResource, getMainClientId, getResourceHeaderWithToken],
  );

  return {
    getMarketIcon,
    getAvailableSubscriptionsGroupedBy,
    loadPartnerSubscriptions,
    getAvailableSubscriptionsWithStatusGroupedBy,
    getPartnerSubscriptionsWithMarketDataInfo,
    subscribe,
  };
};
